import { useCallback, useEffect, useMemo, useState } from "react";
import { Autocomplete, AutocompleteItem } from "@heroui/react";
import { useInfiniteScroll } from "@heroui/use-infinite-scroll";
import { useDebounce } from "../../../utils/utils";

// Local Imports

export const SelectDropdown = ({
  options,
  label = "Select an option",
  onChange,
  value,
  multipleSelection,
  required = false,
  name,
  disabledKeys,
  disabled = false,
  loading = false,
  error = false,
  errorMsg,
  description,
  renderItem,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(0);
  const itemsPerPage = 80;
  const totalItems = options?.length;

  // Check if there are more items to load
  const hasMore = page * itemsPerPage < totalItems;

  const onLoadMore = useCallback(() => {
    if (!hasMore) return;
    const nextPage = page + 1;
    const newItems = options?.slice(0, nextPage * itemsPerPage);
    setItems(newItems || []);
    setPage(nextPage);
  }, [hasMore, options, page]);

  const [, scrollerRef] = useInfiniteScroll({
    hasMore,
    isEnabled: isOpen,
    shouldUseLoader: false,
    onLoadMore,
  });

  // Debounced input value
  const [inputValue, setInputValue] = useState("");
  const debouncedInputValue = useDebounce(inputValue, 200);

  // Filter options based on debounced input
  useEffect(() => {
    if (debouncedInputValue) {
      const filteredItems = options?.filter(option =>
        option?.name?.toLowerCase()?.includes(debouncedInputValue?.toLowerCase())
      );
      setItems(filteredItems || []);
    } else {
      setItems((options?.slice(0, itemsPerPage)) || []);
    }
  }, [debouncedInputValue, options]);

  useEffect(() => {
    setItems((options?.slice(0, itemsPerPage)) || []);
  }, [options]);

  const multipleSelectedKeys = multipleSelection
    ? (Array.isArray(value) ? new Set(value.map((v) => String(v))) : value)
    : undefined;

  const selectedSingleKey =
    !multipleSelection && value !== undefined && value !== null && value !== ""
      ? String(value)
      : null;

  const visibleItems = useMemo(() => {
    if (multipleSelection || selectedSingleKey === null) return items;

    const selectedOption = options?.find((option) => String(option.id) === selectedSingleKey);
    if (!selectedOption) return items;

    if (items.some((item) => String(item.id) === selectedSingleKey)) {
      return items;
    }

    return [selectedOption, ...items];
  }, [items, multipleSelection, options, selectedSingleKey]);

  return (
    <Autocomplete
      label={label}
      size="sm"
      scrollRef={scrollerRef}
      selectionMode={multipleSelection ? "multiple" : "single"}
      isRequired={required}
      isDisabled={disabled}
      isLoading={loading}
      disabledKeys={disabledKeys}
      scrollShadowProps={{
        isEnabled: false
      }}
      name={name}
      isInvalid={error}
      errorMessage={errorMsg}
      items={visibleItems}
      {...(multipleSelection
        ? { selectedKeys: multipleSelectedKeys }
        : { selectedKey: selectedSingleKey })}
      onSelectionChange={(e) => {
        onChange({ target: { name, value: e } })
      }}
      onInputChange={(e) => setInputValue(e)}
      onOpenChange={setIsOpen}
      inputProps={{
        classNames: {
          input: "ml-1",
          inputWrapper: "h-[48px] shadow-none rounded-md pb-[2px]",
          label: 'scale-90 group-data-[focus=true]:-translate-y-2.5 group-data-[focus=true]:scale-75 group-data-[filled=true]:-translate-y-2.5 group-data-[filled=true]:scale-75 font-sans text-grey',
          errorMessage: 'font-sans text-xs text-danger',
          description: 'font-sans text-xs text-grey',
        },
      }}
      classNames={{
        popoverContent: 'rounded-md bg-white border border-grey/50',
      }}
      description={description}
    >
      {(item) => (
        <AutocompleteItem key={String(item.id)}>
          {renderItem ? renderItem(item) : item.name}
        </AutocompleteItem>
      )}
    </Autocomplete>
  );
}
